import React, { useState, useEffect } from 'react';
import { Grid, TextField } from '@material-ui/core';

import Button from '~/components/Button';
import { CustomAutocomplete } from '~/components/CustomAutocomplete';
import { Card, ValueBadge, SectionLabel, InfoField, ActionsRow } from '../styles';
import { AllocationContractorsResponse, OpportunityResponse } from '~/types';
import { useAllocationContractors } from '~/services/hooks';

export interface AttendanceDetailCardProps {
  orderId: string;
  /** Oportunidade/corrida para exibir dados e buscar prestadores elegíveis */
  opportunity?: OpportunityResponse;
  /** Valor da corrida formatado (ex.: "239,90") */
  rideValue?: string;
  /** ID do prestador atual (se houver associação) */
  providerId?: string;
  onAccept: (contractorId: string) => void;
  onReject: () => void;
  loading?: boolean;
}

function formatAddressFromCoordinates(coords: OpportunityResponse['coordinates']): string {
  if (!coords?.length) return '—';
  const source = coords.find(c => c.position === 'SOURCE');
  const dest = coords.find(c => c.position === 'DESTINATION');
  const parts: string[] = [];
  if (source) {
    const addr = [source.address, source.neighborhood, source.city].filter(Boolean).join(', ');
    if (addr) parts.push(`Origem: ${addr}`);
  }
  if (dest) {
    const addr = [dest.address, dest.neighborhood, dest.city].filter(Boolean).join(', ');
    if (addr) parts.push(`Destino: ${addr}`);
  }
  return parts.length ? parts.join(' | ') : '—';
}

function formatVehicleSummary(opp: OpportunityResponse): string {
  const parts: string[] = [];
  if (opp.license_plate) parts.push(`Placa: ${opp.license_plate}`);
  if (opp.vin) parts.push(`Chassi: ${opp.vin}`);
  if (opp.vehicle_model) parts.push(`${opp.vehicle_model}`);
  if (opp.vehicle_color) parts.push(opp.vehicle_color);
  if (opp.vehicle_model_year) parts.push(`Ano ${opp.vehicle_model_year}`);
  return parts.length ? parts.join(' • ') : '—';
}

export function AttendanceDetailCard({
  orderId,
  opportunity,
  rideValue = '0,00',
  providerId,
  onAccept,
  onReject,
  loading = false,
}: AttendanceDetailCardProps) {
  const [redirectContractor, setRedirectContractor] = useState<AllocationContractorsResponse[number] | null>(null);

  const { data: allocationContractors, loading: loadingContractors, load, clear } = useAllocationContractors();

  useEffect(() => {
    if (opportunity?.service_id && opportunity?.customer_vehicle_type) {
      load(opportunity.service_id, opportunity.customer_vehicle_type);
    } else {
      clear();
    }
    return () => {
      setRedirectContractor(null);
    };
  }, [opportunity?.service_id, opportunity?.customer_vehicle_type, load, clear]);

  const contractors = allocationContractors ?? [];
  const contractorToUse = redirectContractor ?? (providerId ? contractors.find(c => c.id === providerId) ?? null : null);
  const selectedContractorId = contractorToUse?.id ?? contractors[0]?.id;
  const canAccept = !!selectedContractorId && contractors.length > 0;

  const handleAccept = () => {
    if (selectedContractorId) onAccept(selectedContractorId);
  };

  const addressText = opportunity ? formatAddressFromCoordinates(opportunity.coordinates) : '—';
  const vehicleText = opportunity ? formatVehicleSummary(opportunity) : '—';

  return (
    <Card>
      <ValueBadge>R$ {rideValue}</ValueBadge>
      <div style={{ fontSize: 14, color: '#2f397c', marginBottom: 16 }}>Novo Atendimento!</div>

      {providerId && (
        <>
          <SectionLabel>Prestador associado</SectionLabel>
          <InfoField>{providerId}</InfoField>
        </>
      )}

      <SectionLabel>Cliente / Ordem</SectionLabel>
      <InfoField>{opportunity?.customer_name ?? '—'}</InfoField>
      <InfoField>Pedido #{opportunity?.number ?? orderId}</InfoField>
      <InfoField>Serviço: {opportunity?.service_description ?? '—'}</InfoField>
      <InfoField>Endereço: {addressText}</InfoField>
      <InfoField>Veículo: {vehicleText}</InfoField>

      <Grid style={{ marginTop: 16 }}>
        <SectionLabel>Direcionar para outro prestador (opcional)</SectionLabel>
        <CustomAutocomplete
          id="redirect-contractor"
          options={contractors}
          getOptionLabel={item => item?.name || item?.company_name || item?.id || ''}
          renderInput={params => (
            <TextField {...params} label="Selecione o prestador" size="small" disabled={loadingContractors} />
          )}
          onChange={(_, value) => setRedirectContractor(value)}
          value={redirectContractor}
          width="100%"
        />
      </Grid>

      <ActionsRow>
        <Button label="Recusar" onClick={onReject} style={{ background: '#FFC000' }} disabled={loading} />
        <Button
          label="Aceitar Atendimento"
          filled
          onClick={handleAccept}
          loading={loading}
          disabled={!canAccept || loading}
        />
      </ActionsRow>
    </Card>
  );
}
